import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Plus, Trash2, CheckCircle2, Eye, Save, ArrowLeft, Image as ImageIcon,
  Type, QrCode, PenTool, Building2, Award, Clock, Lock, Unlock,
  ChevronUp, ChevronDown,
} from "lucide-react";
import api, { BACKEND } from "@/lib/api";
import { Header } from "./Schools";
import { ImageUpload } from "./Content";

const DESIGN_W = 1000;
const DESIGN_H = 707;

// ── Font library ──────────────────────────────────────────────────────────────
export const FONT_GROUPS = [
  { label: "Classic",  fonts: ["Times New Roman", "Georgia", "Garamond"] },
  { label: "Modern",   fonts: ["Helvetica", "Inter", "Open Sans", "Poppins", "Montserrat", "Lato"] },
  { label: "Elegant",  fonts: ["Playfair Display", "Cormorant Garamond", "Cinzel"] },
  { label: "Cursive / Signature", fonts: ["Great Vibes", "Dancing Script", "Allura", "Sacramento", "Parisienne", "Tangerine", "Pacifico", "Kaushan Script", "Satisfy"] },
];

export const ALL_FONTS = FONT_GROUPS.flatMap((g) => g.fonts);

const FONT_CSS = {
  "Times New Roman": "'Times New Roman', Times, serif",
  "Georgia": "Georgia, serif",
  "Garamond": "Garamond, serif",
  "Helvetica": "Helvetica, Arial, sans-serif",
  "Inter": "'Inter', sans-serif",
  "Open Sans": "'Open Sans', sans-serif",
  "Poppins": "'Poppins', sans-serif",
  "Montserrat": "'Montserrat', sans-serif",
  "Lato": "'Lato', sans-serif",
  "Playfair Display": "'Playfair Display', serif",
  "Cormorant Garamond": "'Cormorant Garamond', serif",
  "Cinzel": "'Cinzel', serif",
  "Great Vibes": "'Great Vibes', cursive",
  "Dancing Script": "'Dancing Script', cursive",
  "Allura": "'Allura', cursive",
  "Sacramento": "'Sacramento', cursive",
  "Parisienne": "'Parisienne', cursive",
  "Tangerine": "'Tangerine', cursive",
  "Pacifico": "'Pacifico', cursive",
  "Kaushan Script": "'Kaushan Script', cursive",
  "Satisfy": "'Satisfy', cursive",
};

export const fontCss = (name) => FONT_CSS[name] || name;

// ── Field definitions ─────────────────────────────────────────────────────────
// Fields specific to Teacher Completion certificates
const TEACHER_ONLY_FIELD_META = {
  recipient_name:    { label: "Recipient name",       icon: Type },
  course_name:       { label: "Course name",           icon: Type },
  school_name:       { label: "School name",           icon: Building2 },
  training_date:     { label: "Training date",         icon: Type },
  training_duration: { label: "Training Duration",     icon: Clock },
  training_hours:    { label: "Training hours",        icon: Clock }, // kept for backward compat — maps to training_duration value
};

// Fields specific to Podcast Guest Recognition certificates
const PODCAST_ONLY_FIELD_META = {
  guest_name:      { label: "Guest name",       icon: Type },
  podcast_series:  { label: "Podcast Series",   icon: Type },
  podcast_title:   { label: "Podcast Title",    icon: Type },
  episode_title:   { label: "Episode Title",    icon: Type },
  episode_number:  { label: "Episode No.",      icon: Type },
  episode_theme:   { label: "Theme / Topic",    icon: Type },
  podcast_topic:   { label: "Topic",            icon: Type },
  podcast_language:{ label: "Language",         icon: Type },
  podcast_mode:    { label: "Mode",             icon: Type },
  recording_date:  { label: "Recording Date",   icon: Type },
};

// Fields shared by both certificate types
const SHARED_FIELD_META = {
  issue_date:        { label: "Issue date",            icon: Type },
  certificate_number:{ label: "Certificate number",    icon: Award },
  org_name:          { label: "Organization name",     icon: Building2 },
  org_address:       { label: "Organization address",  icon: Building2 },
  custom_text:       { label: "Custom text",           icon: Type },
  footer:            { label: "Footer",                icon: Type },
  logo:              { label: "Logo",                  icon: ImageIcon },
  signature:         { label: "Signature",             icon: PenTool },
  qr:                { label: "QR code",               icon: QrCode },
};

// Master lookup — used to render labels for ALREADY-PLACED elements regardless of the
// template's current certificate_type, so switching context never breaks existing labels.
const FIELD_META = { ...TEACHER_ONLY_FIELD_META, ...PODCAST_ONLY_FIELD_META, ...SHARED_FIELD_META };

// Which "Add element" buttons to show for a given certificate type
const fieldMetaFor = (certType) =>
  certType === "podcast"
    ? { ...PODCAST_ONLY_FIELD_META, ...SHARED_FIELD_META }
    : { ...TEACHER_ONLY_FIELD_META, ...SHARED_FIELD_META };

const IS_IMAGE_FIELD = new Set(["logo", "signature"]);
const IS_TEXT_FIELD  = (f) => !IS_IMAGE_FIELD.has(f) && f !== "qr";

// Sample values shown in the designer canvas for dynamic fields — matches the exact
// sample data the backend preview endpoints use (preview_certificate_template /
// preview_podcast_certificate), so what an admin sees while dragging elements
// closely matches the real rendered output instead of a generic field-name label.
const SAMPLE_VALUES = {
  recipient_name: "Jane Doe", course_name: "Classroom Management Essentials",
  school_name: "Sample Public School", training_date: "26 Jul 2026",
  training_duration: "16 hours", training_hours: "16 hours",
  guest_name: "Jane Doe", podcast_series: "Voices of Change",
  podcast_title: "Voices of Change — Season 2",
  episode_title: "Reimagining Classroom Learning", episode_number: "12",
  episode_theme: "Innovation in Education",
  podcast_topic: "Innovation in Education", podcast_language: "English",
  podcast_mode: "Online", recording_date: "20 Jul 2026",
  issue_date: "26 Jul 2026", certificate_number: "ER-2026-000001",
  org_name: "Manthan Foundation", org_address: "Bhopal, Madhya Pradesh",
};

const newElement = (field, pageW = DESIGN_W, pageH = DESIGN_H) => ({
  id: `${field}-${Date.now()}`,
  field,
  // Proportional default position (same 38%/42% ratio the old fixed 380/300
  // values worked out to on the 1000x707 default canvas) — this way a new
  // element always lands within the visible canvas regardless of this
  // template's own page size, instead of potentially landing off-screen for
  // a template whose page is smaller than 380x300.
  x: Math.round(pageW * 0.38), y: Math.round(pageH * 0.42),
  width:  IS_IMAGE_FIELD.has(field) || field === "qr" ? 120 : 240,
  height: IS_IMAGE_FIELD.has(field) || field === "qr" ? 80  : 40,
  font_size: 16,
  font_family: "Helvetica",
  font_weight: "normal",
  color: "#0F172A",
  align: "center",
  letter_spacing: 0,
  line_height: 1.2,
  rotation: 0,
  opacity: 1.0,
  layer_order: 0,
  visible: true,
  text: field === "custom_text" || field === "footer" ? "Edit this text" : "",
  image_url: "",
});

// ── Template list ─────────────────────────────────────────────────────────────
export default function CertificateDesigner() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/certificates/templates").then((r) => { setTemplates(r.data || []); setLoading(false); });
  useEffect(() => { load(); }, []);

  const createTemplate = async (certificateType) => {
    const name = window.prompt("Template name?", certificateType === "podcast" ? "New podcast certificate template" : "New certificate template");
    if (!name) return;
    const { data } = await api.post("/certificates/templates", { name, background_url: "", elements: [], certificate_type: certificateType });
    setTemplates((t) => [data, ...t]);
    setEditing(data);
  };

  const activate   = async (id) => { await api.post(`/certificates/templates/${id}/activate`);   toast.success("Template activated");   load(); };
  const deactivate = async (id) => { await api.post(`/certificates/templates/${id}/deactivate`); toast.success("Template deactivated"); load(); };
  const duplicate  = async (id) => { const { data } = await api.post(`/certificates/templates/${id}/duplicate`); toast.success("Duplicated"); setTemplates((t) => [data, ...t]); };
  const remove     = async (id) => { if (!window.confirm("Delete this template?")) return; await api.delete(`/certificates/templates/${id}`); toast.success("Deleted"); load(); };

  if (editing) return <TemplateEditor template={editing} onBack={() => { setEditing(null); load(); }} />;

  return (
    <div className="fade-up">
      <Header title="Certificate Designer" subtitle="Design the certificate layout — no code required." actions={
        <div className="flex items-center gap-2">
          <button onClick={() => createTemplate("completion")} data-testid="add-template" className="inline-flex items-center gap-1.5 bg-[#0F172A] text-white text-sm font-medium px-4 py-2 rounded-lg">
            <Plus className="w-4 h-4" /> New teacher template
          </button>
          <button onClick={() => createTemplate("podcast")} data-testid="add-podcast-template" className="inline-flex items-center gap-1.5 bg-white border border-gray-200 text-[#0F172A] text-sm font-medium px-4 py-2 rounded-lg">
            <Plus className="w-4 h-4" /> New podcast template
          </button>
        </div>
      } />
      {loading ? (
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[0, 1, 2].map((i) => <div key={i} className="h-48 rounded-2xl bg-gray-100 animate-pulse" />)}
        </div>
      ) : templates.length === 0 ? (
        <div className="mt-10 rounded-2xl border border-dashed border-gray-200 bg-white p-12 text-center">
          <Award className="w-8 h-8 text-gray-300 mx-auto mb-3" />
          <div className="font-display text-lg font-medium text-[#0F172A]">No templates yet</div>
          <p className="mt-1 text-sm text-gray-500">Until you activate one, certificates use the original default design.</p>
        </div>
      ) : (
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((t) => (
            <div key={t.id} className="rounded-[24px] border bg-white overflow-hidden" style={{ borderColor: "#E2E8F0" }} data-testid={`template-card-${t.id}`}>
              <div className="h-32 bg-gray-50 grid place-items-center overflow-hidden">
                {t.background_url
                  ? <img src={t.background_url.startsWith("http") ? t.background_url : `${BACKEND}${t.background_url}`} alt={t.name} className="w-full h-full object-cover" />
                  : <Award className="w-8 h-8 text-gray-300" />}
              </div>
              <div className="p-4">
                <div className="flex items-center gap-2">
                  <div className="font-medium text-[#1A202C] truncate flex-1">{t.name}</div>
                  {t.certificate_type === "podcast" && (
                    <span className="inline-flex items-center text-xs font-medium text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded-full shrink-0">Podcast</span>
                  )}
                  {t.active && <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full shrink-0"><CheckCircle2 className="w-3 h-3" /> Active</span>}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button onClick={() => setEditing(t)} className="text-xs font-medium bg-[#EEF2FF] text-[#4F46E5] px-3 py-1.5 rounded-lg">Edit</button>
                  {!t.active
                    ? <button onClick={() => activate(t.id)} data-testid={`activate-${t.id}`} className="text-xs font-medium bg-gray-100 text-gray-700 px-3 py-1.5 rounded-lg">Set active</button>
                    : <button onClick={() => deactivate(t.id)} data-testid={`deactivate-${t.id}`} className="text-xs font-medium bg-amber-50 text-amber-700 px-3 py-1.5 rounded-lg">Deactivate</button>}
                  <button onClick={() => duplicate(t.id)} data-testid={`duplicate-${t.id}`} className="text-xs font-medium bg-gray-100 text-gray-700 px-3 py-1.5 rounded-lg">Duplicate</button>
                  <button onClick={() => remove(t.id)} className="text-xs font-medium text-gray-400 hover:text-rose-600 px-2 py-1.5 rounded-lg ml-auto"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Template editor ────────────────────────────────────────────────────────────
function TemplateEditor({ template, onBack }) {
  const [name, setName]         = useState(template.name);
  const [background, setBg]     = useState(template.background_url || "");
  const [elements, setElements] = useState(template.elements || []);
  const [selectedId, setSelectedId] = useState(null);
  const [saving, setSaving]     = useState(false);
  const certificateType = template.certificate_type || "completion"; // locked at creation — see list page
  const availableFieldMeta = fieldMetaFor(certificateType);
  // This template's own page size. Starts from whatever's already persisted
  // (or the historical 1000x707 default) — but see the useEffect below, which
  // keeps this in sync with the CURRENT background image, not just the one the
  // template happened to have when this component first mounted. That sync is
  // essential: without it, an admin who uploads a new background and THEN
  // positions elements would be positioning them against a canvas size that
  // silently changes again once the backend derives the real page size from
  // that image at save time — exactly the "elements shift/overlap after save"
  // bug this fixes. Both sides use the identical 96 DPI conversion formula, so
  // once this resolves, save-time derivation just confirms the same numbers.
  const [pageW, setPageW] = useState(template.page_width || DESIGN_W);
  const [pageH, setPageH] = useState(template.page_height || DESIGN_H);
  const PAGE_W = pageW;
  const PAGE_H = pageH;

  useEffect(() => {
    if (!background) {
      // Background removed — fall back to whatever was persisted, or the default.
      setPageW(template.page_width || DESIGN_W);
      setPageH(template.page_height || DESIGN_H);
      return;
    }
    const src = background.startsWith("http") ? background : `${BACKEND}${background}`;
    const img = new Image();
    img.onload = () => {
      if (!img.naturalWidth || !img.naturalHeight) return;
      // Same 96 DPI assumption and clamp range the backend's
      // derive_page_size_from_image() uses — must stay identical so the two
      // never diverge.
      const DPI = 96;
      const w = Math.max(200, Math.min(5000, (img.naturalWidth  * 72) / DPI));
      const h = Math.max(200, Math.min(5000, (img.naturalHeight * 72) / DPI));
      setPageW(w);
      setPageH(h);
    };
    img.src = src;
  }, [background]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Dynamic canvas scale based on container width ──────────────────────────
  const containerRef = useRef(null);
  const canvasRef    = useRef(null);
  const [canvasScale, setCanvasScale] = useState(0.7);
  const [manualZoom, setManualZoom] = useState(false); // true once admin overrides auto-fit
  const drag = useRef(null);

  // Recompute scale whenever the container resizes — only while in auto-fit mode
  const updateScale = useCallback(() => {
    if (!containerRef.current || manualZoom) return;
    const available = containerRef.current.clientWidth - 32; // subtract padding
    const scale = Math.min(0.85, Math.max(0.3, available / PAGE_W));
    setCanvasScale(scale);
  }, [manualZoom, PAGE_W]);

  const zoomBy = (delta) => {
    setManualZoom(true);
    setCanvasScale((s) => Math.min(2.5, Math.max(0.2, +(s + delta).toFixed(2))));
  };
  const resetZoom = () => {
    setManualZoom(false);
    if (containerRef.current) {
      const available = containerRef.current.clientWidth - 32;
      setCanvasScale(Math.min(0.85, Math.max(0.3, available / PAGE_W)));
    }
  };

  useLayoutEffect(() => {
    updateScale();
    const ro = new ResizeObserver(updateScale);
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [updateScale]);

  const selected = elements.find((e) => e.id === selectedId) || null;
  const update   = (id, patch) => setElements((els) => els.map((e) => e.id === id ? { ...e, ...patch } : e));

  const addElement = (field) => {
    const el = newElement(field, PAGE_W, PAGE_H);
    const maxLayer = elements.reduce((m, e) => Math.max(m, e.layer_order || 0), 0);
    el.layer_order = maxLayer + 1;
    setElements((els) => [...els, el]);
    setSelectedId(el.id);
  };

  const removeElement = (id) => {
    setElements((els) => els.filter((e) => e.id !== id));
    if (selectedId === id) setSelectedId(null);
  };

  // ── Drag — uses live canvasScale ref so coords are always correct ──────────
  const scaleRef = useRef(canvasScale);
  useEffect(() => { scaleRef.current = canvasScale; }, [canvasScale]);
  const pageRef = useRef({ w: PAGE_W, h: PAGE_H });
  useEffect(() => { pageRef.current = { w: PAGE_W, h: PAGE_H }; }, [PAGE_W, PAGE_H]);

  const onPointerDown = (e, el) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedId(el.id);
    // Capture pointer so events keep flowing even if cursor/finger leaves the element
    e.target.setPointerCapture?.(e.pointerId);
    const rect = canvasRef.current.getBoundingClientRect();
    drag.current = {
      id: el.id,
      pointerId: e.pointerId,
      target: e.target,
      offsetX: (e.clientX - rect.left) / scaleRef.current - el.x,
      offsetY: (e.clientY - rect.top)  / scaleRef.current - el.y,
    };
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
  };

  const onPointerMove = useCallback((e) => {
    if (!drag.current || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const s = scaleRef.current;
    const { w: pw, h: ph } = pageRef.current;
    const x = Math.max(0, Math.min(pw, (e.clientX - rect.left) / s - drag.current.offsetX));
    const y = Math.max(0, Math.min(ph, (e.clientY - rect.top)  / s - drag.current.offsetY));
    update(drag.current.id, { x: Math.round(x), y: Math.round(y) });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onPointerUp = useCallback((e) => {
    if (drag.current?.target) {
      drag.current.target.releasePointerCapture?.(drag.current.pointerId);
    }
    drag.current = null;
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", onPointerUp);
  }, [onPointerMove]);

  // Sanitize elements for API calls — Pydantic rejects null/NaN for non-Optional numeric fields
  const sanitizeElements = (els) => els.map((el) => ({
    ...el,
    x: Number.isFinite(el.x) ? el.x : 0,
    y: Number.isFinite(el.y) ? el.y : 0,
    width: Number.isFinite(el.width) ? el.width : 200,
    height: Number.isFinite(el.height) ? el.height : 40,
    font_size: Number.isFinite(el.font_size) ? el.font_size : 14,
    letter_spacing: Number.isFinite(el.letter_spacing) ? el.letter_spacing : 0,
    line_height: Number.isFinite(el.line_height) ? el.line_height : 1.2,
    rotation: Number.isFinite(el.rotation) ? el.rotation : 0,
    opacity: Number.isFinite(el.opacity) ? el.opacity : 1,
    layer_order: Number.isFinite(el.layer_order) ? Math.round(el.layer_order) : 0,
    text: el.text ?? "",
    image_url: el.image_url ?? "",
  }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/certificates/templates/${template.id}`, { name, background_url: background, elements: sanitizeElements(elements), certificate_type: certificateType });
      toast.success("Template saved");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map((d) => d.msg || JSON.stringify(d)).join("; ") : (typeof detail === "string" ? detail : "Save failed");
      toast.error(msg);
    }
    setSaving(false);
  };

  const preview = async () => {
    try {
      const { data } = await api.post("/certificates/templates/preview", {
        name, background_url: background, elements: sanitizeElements(elements), certificate_type: certificateType,
        page_width: template.page_width || null, page_height: template.page_height || null,
      }, { responseType: "blob" });
      window.open(URL.createObjectURL(data), "_blank");
    } catch { toast.error("Preview failed"); }
  };

  const sortedElements = [...elements].sort((a, b) => (a.layer_order || 0) - (b.layer_order || 0));
  const bgSrc = background
    ? (background.startsWith("http") ? background : `${BACKEND}${background}`)
    : undefined;

  return (
    <div className="fade-up">
      <Header title={name} subtitle={
        <span className="inline-flex items-center gap-2">
          Drag elements to position them. Nothing is saved until you click Save.
          <span className={`inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-full ${certificateType === "podcast" ? "text-indigo-700 bg-indigo-50" : "text-gray-600 bg-gray-100"}`}>
            {certificateType === "podcast" ? "Podcast Guest Certificate" : "Teacher Completion Certificate"}
          </span>
        </span>
      } actions={
        <div className="flex flex-wrap gap-2">
          <button onClick={onBack} className="inline-flex items-center gap-1.5 bg-white border border-gray-200 text-sm font-medium px-3 py-2 rounded-lg">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <button onClick={preview} data-testid="template-preview" className="inline-flex items-center gap-1.5 bg-white border border-gray-200 text-sm font-medium px-3 py-2 rounded-lg">
            <Eye className="w-4 h-4" /> Preview PDF
          </button>
          <button onClick={save} disabled={saving} data-testid="template-save" className="inline-flex items-center gap-1.5 bg-[#0F172A] text-white text-sm font-medium px-3 py-2 rounded-lg disabled:opacity-50">
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      } />

      <div className="mt-4 grid gap-4">
        {/* Settings row */}
        <div className="rounded-2xl border border-gray-200 bg-white p-4 grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-gray-500 block mb-1.5">Template name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} className="px-3 py-2 rounded-lg border border-gray-200 text-sm outline-none w-full" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-gray-500 block mb-1.5">Background image</label>
            <ImageUpload value={background} onChange={setBg} testid="template-bg-upload" />
          </div>
        </div>

        {/* Add elements row */}
        <div className="rounded-2xl border border-gray-200 bg-white p-3">
          <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">Add element</p>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(availableFieldMeta).map(([field, meta]) => (
              <button key={field} onClick={() => addElement(field)} data-testid={`add-el-${field}`}
                className="inline-flex items-center gap-1 text-xs font-medium bg-white border border-gray-200 px-2.5 py-1.5 rounded-lg hover:border-[#4F46E5] hover:text-[#4F46E5] transition-colors">
                <meta.icon className="w-3 h-3 shrink-0" /> {meta.label}
              </button>
            ))}
          </div>
        </div>

        {/* Canvas + Properties — stack on small screens, side-by-side on lg */}
        <div className="grid lg:grid-cols-[1fr_320px] gap-4 items-start">

          {/* ── Canvas ── */}
          <div ref={containerRef} className="overflow-auto rounded-2xl border border-gray-200 bg-[#f0f0f0] p-4 min-w-0" style={{ maxHeight: "78vh" }}>
            <div
              ref={canvasRef}
              className="relative bg-white shadow-xl mx-auto select-none"
              style={{
                width:  PAGE_W * canvasScale,
                height: PAGE_H * canvasScale,
                cursor: "default",
                touchAction: "none",
              }}
              onClick={() => setSelectedId(null)}
              data-testid="design-canvas"
            >
              {/* Background — object-fit:contain shows the COMPLETE image regardless of
                  orientation (portrait/landscape/square). Never crops, never stretches.
                  The canvas itself is sized to this template's own page_width/page_height
                  (falling back to 1000x707 for templates that predate that field), so for
                  a template whose page size was derived from this exact image, the canvas
                  and the image share the same aspect ratio and there's no letterboxing at
                  all — contain is kept as a safe fallback for older/mismatched templates.
                  Element x/y/width/height map directly onto this same coordinate space —
                  no scaling happens anywhere, matching the PDF renderer exactly. */}
              {bgSrc && (
                <img
                  src={bgSrc}
                  alt=""
                  draggable={false}
                  className="absolute inset-0 w-full h-full pointer-events-none select-none"
                  style={{ objectFit: "contain", objectPosition: "center" }}
                  data-testid="design-canvas-bg"
                />
              )}
              {sortedElements.map((el) => (
                <div
                  key={el.id}
                  onPointerDown={(e) => onPointerDown(e, el)}
                  onClick={(e) => { e.stopPropagation(); setSelectedId(el.id); }}
                  className={`absolute flex items-center cursor-move select-none border-2 transition-colors ${
                    selectedId === el.id
                      ? "border-[#3B68E7] shadow-[0_0_0_1px_#3B68E7]"
                      : "border-transparent hover:border-gray-300"
                  }`}
                  style={{
                    left:   el.x * canvasScale,
                    top:    el.y * canvasScale,
                    width:  el.width  * canvasScale,
                    height: el.height * canvasScale,
                    justifyContent: el.align === "center" ? "center" : el.align === "right" ? "flex-end" : "flex-start",
                    opacity: el.visible ? (el.opacity ?? 1) : 0.2,
                    transform: el.rotation ? `rotate(${el.rotation}deg)` : undefined,
                    transformOrigin: "center",
                    zIndex: el.layer_order || 0,
                    overflow: "hidden",
                    touchAction: "none",
                  }}
                  data-testid={`el-${el.id}`}
                >
                  {IS_IMAGE_FIELD.has(el.field) ? (
                    el.image_url
                      ? <img src={el.image_url.startsWith("http") ? el.image_url : `${BACKEND}${el.image_url}`}
                          alt="" className="w-full h-full object-contain" />
                      : <div className="w-full h-full bg-gray-100/90 grid place-items-center text-[9px] text-gray-500 font-medium">
                          {FIELD_META[el.field]?.label}
                        </div>
                  ) : el.field === "qr" ? (
                    <div className="w-full h-full bg-gray-100/90 grid place-items-center">
                      <QrCode className="text-gray-400" style={{ width: Math.min(el.width, el.height) * canvasScale * 0.7 }} />
                    </div>
                  ) : (
                    <span
                      className="whitespace-pre w-full"
                      style={{
                        fontSize:      el.font_size * canvasScale,
                        fontWeight:    el.font_weight === "bold" ? 700 : 400,
                        fontStyle:     el.font_style === "italic" ? "italic" : "normal",
                        textDecoration: el.underline ? "underline" : "none",
                        fontFamily:    fontCss(el.font_family || "Helvetica"),
                        color:         el.color || "#0F172A",
                        letterSpacing: el.letter_spacing ? `${el.letter_spacing * canvasScale}px` : undefined,
                        lineHeight:    el.line_height || 1.2,
                        textAlign:     el.align || "left",
                      }}
                    >
                      {el.text || SAMPLE_VALUES[el.field] || FIELD_META[el.field]?.label}
                    </span>
                  )}

                  {/* Selected indicator label */}
                  {selectedId === el.id && (
                    <div
                      className="absolute -top-5 left-0 bg-[#3B68E7] text-white text-[9px] px-1.5 py-0.5 rounded-t font-medium whitespace-nowrap pointer-events-none"
                      style={{ fontSize: 9 }}
                    >
                      {FIELD_META[el.field]?.label}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Zoom controls — lets admin zoom in for comfortable editing of tall/portrait backgrounds */}
            <div className="mt-2 flex items-center justify-center gap-2 text-[11px] text-gray-500">
              <span>{Math.round(PAGE_W)}×{Math.round(PAGE_H)}px</span>
              <button type="button" onClick={() => zoomBy(-0.1)} data-testid="canvas-zoom-out"
                className="w-6 h-6 grid place-items-center rounded-md border border-gray-200 bg-white hover:bg-gray-50 text-gray-600 font-bold leading-none">−</button>
              <span className="w-10 text-center tabular-nums">{Math.round(canvasScale * 100)}%</span>
              <button type="button" onClick={() => zoomBy(0.1)} data-testid="canvas-zoom-in"
                className="w-6 h-6 grid place-items-center rounded-md border border-gray-200 bg-white hover:bg-gray-50 text-gray-600 font-bold leading-none">+</button>
              <button type="button" onClick={resetZoom} data-testid="canvas-zoom-fit"
                className="px-2 h-6 rounded-md border border-gray-200 bg-white hover:bg-gray-50 text-gray-600 font-medium">Fit</button>
            </div>
          </div>

          {/* ── Properties panel ── */}
          <div className="rounded-2xl border border-gray-200 bg-white overflow-y-auto" style={{ maxHeight: "min(80vh, 720px)" }}>
            {!selected ? (
              <div className="p-4">
                <p className="text-sm text-gray-500">Select or add an element to edit its properties.</p>
                {elements.length > 0 && (
                  <div className="mt-4 space-y-1">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">Elements ({elements.length})</p>
                    {[...elements].sort((a, b) => (b.layer_order || 0) - (a.layer_order || 0)).map((el) => (
                      <button
                        key={el.id}
                        onClick={() => setSelectedId(el.id)}
                        className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-50 text-left text-sm"
                      >
                        {(() => { const M = FIELD_META[el.field]; return M ? <M.icon className="w-3.5 h-3.5 text-gray-400 shrink-0" /> : null; })()}
                        <span className="truncate text-gray-700">{FIELD_META[el.field]?.label}</span>
                        {!el.visible && <span className="ml-auto text-[10px] text-gray-400">hidden</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <ElementProperties
                el={selected}
                onChange={(patch) => update(selected.id, patch)}
                onRemove={() => removeElement(selected.id)}
                maxLayer={elements.reduce((m, e) => Math.max(m, e.layer_order || 0), 0)}
              />
            )}
          </div>
        </div>
      </div>

      <style>{`.propinput { width:100%; padding:6px 10px; border-radius:8px; border:1px solid #E2E8F0; font-size:13px; outline:none; background:#fff; box-sizing:border-box; } .propinput:focus { border-color:#4F46E5; }`}</style>
    </div>
  );
}

// ── Element properties panel ───────────────────────────────────────────────────
function ElementProperties({ el, onChange, onRemove, maxLayer }) {
  const [aspectLocked, setAspectLocked] = useState(true);
  const aspectRatio = el.width / (el.height || 1);

  const handleWidth = (val) => {
    const w = Number(val);
    if (aspectLocked && IS_IMAGE_FIELD.has(el.field)) {
      onChange({ width: w, height: Math.round(w / aspectRatio) });
    } else {
      onChange({ width: w });
    }
  };

  const handleHeight = (val) => {
    const h = Number(val);
    if (aspectLocked && IS_IMAGE_FIELD.has(el.field)) {
      onChange({ width: Math.round(h * aspectRatio), height: h });
    } else {
      onChange({ height: h });
    }
  };

  return (
    <div className="grid gap-3 p-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-[#1A202C]">{FIELD_META[el.field]?.label}</div>
        <button onClick={onRemove} className="text-gray-400 hover:text-rose-600 p-1 rounded" title="Remove element">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {/* Visibility */}
      <label className="inline-flex items-center gap-2 text-xs cursor-pointer select-none">
        <input type="checkbox" checked={el.visible !== false} onChange={(e) => onChange({ visible: e.target.checked })} />
        Visible
      </label>

      {/* Custom text / footer — editable override */}
      {(el.field === "custom_text" || el.field === "footer") && (
        <PropField label="Text">
          <textarea
            rows={2}
            value={el.text || ""}
            onChange={(e) => onChange({ text: e.target.value })}
            className="propinput resize-none"
            placeholder="Enter text…"
          />
        </PropField>
      )}
      {/* training_duration / training_hours — always auto-filled, no manual override */}
      {(el.field === "training_hours" || el.field === "training_duration") && (
        <div className="rounded-lg bg-indigo-50 border border-indigo-100 px-3 py-2.5 text-xs text-indigo-700">
          ⚡ Auto-filled from the program's Duration field. No manual entry needed.
        </div>
      )}

      {/* Image upload */}
      {IS_IMAGE_FIELD.has(el.field) && (
        <PropField label="Image">
          <ImageUpload value={el.image_url || ""} onChange={(v) => onChange({ image_url: v })} testid={`el-img-${el.id}`} />
          {el.image_url && (
            <button type="button" onClick={() => onChange({ image_url: "" })}
              className="mt-1 text-xs text-rose-600 hover:underline">Remove image</button>
          )}
        </PropField>
      )}

      <Divider label="Position" />

      {/* Position */}
      <div className="grid grid-cols-2 gap-2">
        <PropField label="X (px)">
          <input type="number" value={el.x ?? 0} onChange={(e) => onChange({ x: Number(e.target.value) })} className="propinput" />
        </PropField>
        <PropField label="Y (px)">
          <input type="number" value={el.y ?? 0} onChange={(e) => onChange({ y: Number(e.target.value) })} className="propinput" />
        </PropField>
      </div>

      {/* Size */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-medium text-gray-500">Size</span>
          {IS_IMAGE_FIELD.has(el.field) && (
            <button
              onClick={() => setAspectLocked((v) => !v)}
              className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border ${aspectLocked ? "bg-[#EEF2FF] border-[#C7D2FE] text-[#4F46E5]" : "bg-gray-50 border-gray-200 text-gray-400"}`}
            >
              {aspectLocked ? <Lock className="w-3 h-3" /> : <Unlock className="w-3 h-3" />}
              {aspectLocked ? "Locked" : "Free"}
            </button>
          )}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <PropField label="Width (px)">
            <input type="number" value={el.width ?? 100} onChange={(e) => handleWidth(e.target.value)} className="propinput" />
          </PropField>
          <PropField label="Height (px)">
            <input type="number" value={el.height ?? 40} onChange={(e) => handleHeight(e.target.value)} className="propinput" />
          </PropField>
        </div>
      </div>

      {/* Text controls */}
      {IS_TEXT_FIELD(el.field) && (
        <>
          <Divider label="Typography" />

          <PropField label="Font family">
            <select
              value={el.font_family || "Helvetica"}
              onChange={(e) => onChange({ font_family: e.target.value })}
              className="propinput"
              style={{ fontFamily: fontCss(el.font_family || "Helvetica") }}
            >
              {FONT_GROUPS.map((group) => (
                <optgroup key={group.label} label={group.label}>
                  {group.fonts.map((f) => (
                    <option key={f} value={f} style={{ fontFamily: fontCss(f) }}>{f}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </PropField>

          {/* Font preview */}
          <div
            className="px-3 py-2 rounded-lg bg-gray-50 text-sm text-center truncate border border-gray-100"
            style={{ fontFamily: fontCss(el.font_family || "Helvetica"), fontSize: 14, color: el.color || "#0F172A" }}
          >
            {el.text || "Certificate of Completion"}
          </div>

          <div className="grid grid-cols-2 gap-2">
            <PropField label="Font size">
              <input type="number" min="6" max="120" value={el.font_size ?? 16} onChange={(e) => onChange({ font_size: Number(e.target.value) })} className="propinput" />
            </PropField>
            <PropField label="Weight">
              <select value={el.font_weight || "normal"} onChange={(e) => onChange({ font_weight: e.target.value })} className="propinput">
                <option value="normal">Normal</option>
                <option value="bold">Bold</option>
              </select>
            </PropField>
          </div>

          {/* Bold / Italic / Underline toggles */}
          <div className="flex gap-2">
            {[
              { key: "font_weight", val: "bold",   label: "B", active: el.font_weight === "bold", style: { fontWeight: 700 } },
              { key: "font_style",  val: "italic",  label: "I", active: el.font_style === "italic", style: { fontStyle: "italic" } },
              { key: "underline",   val: true,       label: "U", active: !!el.underline, style: { textDecoration: "underline" } },
            ].map(({ key, val, label, active, style }) => (
              <button
                key={key}
                type="button"
                onClick={() => onChange({ [key]: active ? (key === "underline" ? false : "normal") : val })}
                className={`w-8 h-8 rounded-lg border text-sm grid place-items-center transition-colors ${active ? "bg-[#4F46E5] text-white border-[#4F46E5]" : "border-gray-200 text-gray-600 hover:border-gray-300"}`}
                style={style}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-2">
            <PropField label="Color">
              <input type="color" value={el.color || "#0F172A"} onChange={(e) => onChange({ color: e.target.value })}
                className="w-full h-9 rounded-lg border border-gray-200 cursor-pointer" />
            </PropField>
            <PropField label="Align">
              <select value={el.align || "left"} onChange={(e) => onChange({ align: e.target.value })} className="propinput">
                <option value="left">Left</option>
                <option value="center">Center</option>
                <option value="right">Right</option>
              </select>
            </PropField>
            <PropField label="Letter spacing">
              <input type="number" step="0.5" value={el.letter_spacing ?? 0} onChange={(e) => onChange({ letter_spacing: Number(e.target.value) })} className="propinput" />
            </PropField>
            <PropField label="Line height">
              <input type="number" step="0.1" min="0.8" max="4" value={el.line_height ?? 1.2} onChange={(e) => onChange({ line_height: Number(e.target.value) })} className="propinput" />
            </PropField>
          </div>
        </>
      )}

      {/* Transform */}
      <Divider label="Transform" />
      <div className="grid grid-cols-2 gap-2">
        <PropField label="Rotation (°)">
          <input type="number" min="-360" max="360" value={el.rotation ?? 0} onChange={(e) => onChange({ rotation: Number(e.target.value) })} className="propinput" />
        </PropField>
        <PropField label="Opacity (0–1)">
          <input type="number" min="0" max="1" step="0.05" value={el.opacity ?? 1} onChange={(e) => onChange({ opacity: Number(e.target.value) })} className="propinput" />
        </PropField>
      </div>

      {/* Layer */}
      <Divider label="Layer" />
      <div className="grid grid-cols-2 gap-2">
        <PropField label="Layer order">
          <input type="number" value={el.layer_order ?? 0} onChange={(e) => onChange({ layer_order: Number(e.target.value) })} className="propinput" />
        </PropField>
        <div className="flex flex-col gap-1 pt-5">
          <button onClick={() => onChange({ layer_order: maxLayer + 1 })} className="flex items-center gap-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-2 py-1 rounded-lg">
            <ChevronUp className="w-3 h-3" /> Front
          </button>
          <button onClick={() => onChange({ layer_order: 0 })} className="flex items-center gap-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-2 py-1 rounded-lg">
            <ChevronDown className="w-3 h-3" /> Back
          </button>
        </div>
      </div>

      {/* Reset */}
      <Divider label="" />
      <button
        type="button"
        onClick={() => {
          const fresh = newElement(el.field);
          onChange({ ...fresh, id: el.id, x: el.x, y: el.y, image_url: el.image_url || "", text: el.text || "" });
        }}
        className="text-xs text-gray-400 hover:text-rose-600 text-left"
      >
        Reset element to defaults
      </button>
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────────
const PropField = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-medium text-gray-500 block mb-1">{label}</span>
    {children}
  </label>
);

const Divider = ({ label }) => (
  <div className="flex items-center gap-2 mt-1">
    {label && <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 shrink-0">{label}</span>}
    <div className="flex-1 h-px bg-gray-100" />
  </div>
);

// ── Read-only certificate canvas preview ────────────────────────────────────────
// Renders a template + resolved values as static positioned HTML — no drag, no
// selection, no editing. Uses the EXACT same positioning math as the interactive
// Designer canvas above (x*scale, y*scale, flex align-items:center, whitespace-pre)
// so this preview is pixel-consistent with both the Designer's own canvas and the
// final PDF (which shares this template's own page_width×page_height coordinate
// space — no scaling happens anywhere, so all three stay pixel-consistent). This is
// what lets a certificate be previewed as pure HTML — no PDF is ever generated for
// a preview; PDF generation only happens on approval or download (see backend).
//
// `values` mirrors the backend's values dict (e.g. { guest_name: "...", org_name:
// "...", qr: "<verify url or empty>" }). Dynamic fields (matching _dynamic_fields
// in certificates.py) always read from `values`; custom_text/footer use the
// element's own saved text first, falling back to `values` — identical priority
// to the backend renderer.
const PREVIEW_DYNAMIC_FIELDS = new Set([
  "recipient_name", "course_name", "school_name", "training_date",
  "issue_date", "certificate_number", "org_name", "org_address",
  "training_hours", "training_duration",
  "guest_name", "podcast_series", "episode_title", "episode_number", "episode_theme",
  "podcast_title", "podcast_topic", "podcast_language", "podcast_mode", "recording_date",
]);

export function CertificateCanvasPreview({ template, values = {}, maxWidth = 720 }) {
  const elements = template?.elements || [];
  const bgUrl = template?.background_url || "";
  const bgSrc = bgUrl ? (bgUrl.startsWith("http") ? bgUrl : `${BACKEND}${bgUrl}`) : "";
  const pageW = template?.page_width || DESIGN_W;
  const pageH = template?.page_height || DESIGN_H;
  const scale = maxWidth / pageW;
  const sorted = [...elements].sort((a, b) => (a.layer_order || 0) - (b.layer_order || 0));

  const textFor = (el) => {
    if (PREVIEW_DYNAMIC_FIELDS.has(el.field)) return values[el.field] || "";
    return (el.text || "").trim() || values[el.field] || "";
  };

  return (
    <div
      className="relative bg-white shadow-md mx-auto overflow-hidden"
      style={{ width: pageW * scale, height: pageH * scale }}
      data-testid="certificate-preview-canvas"
    >
      {bgSrc && (
        <img src={bgSrc} alt="" className="absolute inset-0 w-full h-full pointer-events-none select-none"
          style={{ objectFit: "contain", objectPosition: "center" }} />
      )}
      {sorted.map((el) => {
        if (el.visible === false) return null;
        const style = {
          left: el.x * scale, top: el.y * scale,
          width: el.width * scale, height: el.height * scale,
          justifyContent: el.align === "center" ? "center" : el.align === "right" ? "flex-end" : "flex-start",
          opacity: el.opacity ?? 1,
          transform: el.rotation ? `rotate(${el.rotation}deg)` : undefined,
          transformOrigin: "center",
          zIndex: el.layer_order || 0,
          overflow: "hidden",
        };
        if (IS_IMAGE_FIELD.has(el.field)) {
          const src = el.image_url || (el.field === "logo" ? values.logo : "");
          return (
            <div key={el.id} className="absolute flex items-center" style={style}>
              {src ? <img src={src.startsWith("http") ? src : `${BACKEND}${src}`} alt="" className="w-full h-full object-contain" /> : null}
            </div>
          );
        }
        if (el.field === "qr") {
          return (
            <div key={el.id} className="absolute flex items-center justify-center bg-gray-50 border border-dashed border-gray-200" style={style}>
              {values.qr
                ? <QrCode className="text-gray-400" style={{ width: Math.min(el.width, el.height) * scale * 0.7 }} />
                : <span className="text-[9px] text-gray-400">QR</span>}
            </div>
          );
        }
        const text = textFor(el);
        if (!text) return null;
        return (
          <div key={el.id} className="absolute flex items-center" style={style}>
            <span
              className="whitespace-pre w-full"
              style={{
                fontSize: el.font_size * scale,
                fontWeight: el.font_weight === "bold" ? 700 : 400,
                fontStyle: el.font_style === "italic" ? "italic" : "normal",
                textDecoration: el.underline ? "underline" : "none",
                fontFamily: fontCss(el.font_family || "Helvetica"),
                color: el.color || "#0F172A",
                letterSpacing: el.letter_spacing ? `${el.letter_spacing * scale}px` : undefined,
                lineHeight: el.line_height || 1.2,
                textAlign: el.align || "left",
              }}
            >
              {text}
            </span>
          </div>
        );
      })}
    </div>
  );
}
